import React, { useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { Column } from 'primereact/column'
import { PrimeIcons } from 'primereact/api'
import { Button } from 'primereact/button'
import { OverlayPanel } from 'primereact/overlaypanel'
import { Calendar } from 'primereact/calendar'
import { endOfDay, startOfDay, subMonths, subYears } from 'date-fns'

import { INITIAL_DEFAULT_TABLE_ROWS, Table } from '../../uikit/Table'
import useText from '../../hooks/useText'
import { RootState } from '../../../partner/redux/store'
import {
    RejectedAccess,
    useNfcTagCreateMutation,
    useNfcTagGetRejectedTagsQuery,
    NfcTagGetRejectedTagsApiArg,
    usePartnerGetCompaniesByPartnerIdQuery,
    useChargePointDeleteRejectedTagMutation,
} from '../../redux/api/web-api'
import { dateLocalize } from '../../../lib/date-localize'
import { Input } from '../../uikit/Input'
import { isAdminDomain, isPartnerDomain } from '../../../lib/currentDomain'
import isDawn from '../../../lib/isDawn'
import isLuna from '../../../lib/isLuna'
import getNfcLocked from '../../../lib/charge-point/getNfcLocked'
import ChargePointExpandOptions from '../../../lib/chargePointExpandOptions'
import { useChargePointsQuery } from '../../hooks/useFetchEndpoints'
import { Dropdown } from '../../uikit/Dropdown'
import { useToast } from '../../uikit/ToastProvider'
import { confirm } from '../../uikit/ConfirmDialog'

import { AddRFIDDialog } from './dialogs'

type RejectedAccessRow = RejectedAccess & { rowKey: string }

const toRejectedAccessRow = (rejectedAccess: RejectedAccess): RejectedAccessRow => ({
    ...rejectedAccess,
    rowKey: JSON.stringify([
        rejectedAccess.nfcTagId,
        rejectedAccess.chargePointId,
        rejectedAccess.connectorId,
        rejectedAccess.timeStamp,
    ]),
})

export const RegisterRfidTagWidget = () => {
    const text = useText()
    const { partnerId, selectedCompanyId } = useSelector((state: RootState) => state.user)
    const [createRfidTag] = useNfcTagCreateMutation()

    const toast = useToast()

    const [addRfidTagIsOpened, setAddRfidTagIsOpened] = useState(false)
    const [selectedTags, setSelectedTags] = useState<RejectedAccessRow[]>([])
    const [globalFilter, setGlobalFilter] = useState('')

    const onAddRfidTag = () => {
        if (isAdminDomain) {
            handleAddSelected(selectedCompanyId)
        } else if (isPartnerDomain) {
            setAddRfidTagIsOpened(true)
        }
    }

    const handleAddSelected = (organisationId: string) => {
        setAddRfidTagIsOpened(false)
        const selectedTagsWithIds = selectedTags.filter((item): item is RejectedAccessRow & { nfcTagId: string } =>
            Boolean(item.nfcTagId)
        )

        if (selectedTagsWithIds.length !== selectedTags.length) {
            toast.notifyError(text('msgRFIDTagCouldNotBeAdded'))
            return
        }

        const tags = selectedTagsWithIds.map((item) => ({
            nfcTag: {
                id: item.nfcTagId,
                partnerId,
                companyId: organisationId,
                active: true,
            },
        }))

        Promise.all(tags.map((tag) => createRfidTag(tag).unwrap()))
            .then(() => {
                toast.notifySuccess(text('msgRFIDTagAdded'))

                setSelectedTags([])
            })
            .catch(() => toast.notifyError(text('msgRFIDTagCouldNotBeAdded')))
    }

    const op = useRef<OverlayPanel>(null)
    const { data: chargePoints, isLoading: isLoadingChargePoints } = useChargePointsQuery({
        partnerId,
        companyId: selectedCompanyId,
        expand: ChargePointExpandOptions.OcppConfig,
    })

    const { data: organisations, isLoading: isLoadingOrganisations } = usePartnerGetCompaniesByPartnerIdQuery(
        { partnerId: partnerId! },
        { skip: !partnerId }
    )

    const [filters, setFilters] = useState<NfcTagGetRejectedTagsApiArg>({
        noOfItems: INITIAL_DEFAULT_TABLE_ROWS,
        page: 0,
        partnerId: isPartnerDomain ? partnerId : undefined,
        chargePointId: undefined,
        companyId: isAdminDomain ? selectedCompanyId : undefined,
        from: subMonths(new Date(), 1).toISOString(),
        to: undefined,
    })

    const { data: rejectedTags, isLoading: rejectedTagsAreLoading } = useNfcTagGetRejectedTagsQuery(filters, {
        refetchOnMountOrArgChange: true,
    })

    const [deleteRejectedTag] = useChargePointDeleteRejectedTagMutation()

    const handleDeleteRejectedRFIDTag = async (rfidTag: RejectedAccess) => {
        if (!rfidTag) return

        const confirmed = await confirm({
            header: text('lblProceedConfirmation'),
            message: text('msgDoYouWantToDeleteRejectedRFID', { id: rfidTag.nfcTagId }),
            variant: 'danger',
        })
        if (!confirmed) return

        deleteRejectedTag({
            chargePointId: rfidTag.chargePointId!,
            nfcTagId: rfidTag.nfcTagId!,
        })
            .unwrap()
            .then(() => {
                toast.notifySuccess(text('msgRejectedRFIDDeleted'))
            })
            .catch(() => {
                toast.notifyError(text('msgRejectedRFIDNotDeleted'))
            })
    }

    const applyFilter = (filter: Partial<NfcTagGetRejectedTagsApiArg>) => {
        setFilters((prev) => ({
            ...prev,
            ...filter,
            page: 0,
        }))
    }

    const filteredData = (rejectedTags?.items ?? [])
        .filter((item) => {
            if (!globalFilter) return true
            const search = globalFilter.toLowerCase()
            return item.nfcTagId?.toLowerCase()?.includes(search) || item.chargePointId?.toLowerCase()?.includes(search)
        })
        .map(toRejectedAccessRow)

    const selectedCharger = chargePoints?.items?.find((item) => item.id === filters.chargePointId)
    const page = filters.page ?? 0
    const rows = filters.noOfItems ?? INITIAL_DEFAULT_TABLE_ROWS

    return (
        <>
            <div className="flex flex-wrap gap-2 justify-content-end align-items-center mb-4">
                <div className="flex gap-2 align-items-center justify-content-between">
                    <Button
                        disabled={selectedTags.length === 0}
                        onClick={onAddRfidTag}
                        label={text('btnAddSelected')}
                        icon="pi pi-plus"
                        data-testid="add-selected-tags-btn"
                    />
                    <Input
                        id="organisation-search-input"
                        type="search"
                        value={globalFilter}
                        onChange={(e) => setGlobalFilter(e.target.value)}
                        placeholder={text('lblSearch')}
                        icon={PrimeIcons.SEARCH}
                        data-testid="search-input"
                    />
                    <Button
                        icon="pi pi-filter"
                        rounded
                        text
                        onClick={(e) => op.current?.toggle(e)}
                        data-testid="filter-btn"
                    />

                    <OverlayPanel ref={op} closeOnEscape className="px-2 max-w-25rem" data-testid="filter-panel">
                        <div className="field col-12">
                            <Dropdown
                                label={text('lblChargePointId')}
                                value={filters.chargePointId}
                                options={chargePoints?.items?.map(({ id }) => ({ value: id, label: id }))}
                                onChange={(e) => applyFilter({ chargePointId: e.value })}
                                checkmark={true}
                                highlightOnSelect={false}
                                filter
                                id="charge-point-select"
                                className="w-full"
                                showClear
                                loading={isLoadingChargePoints}
                                data-testid="charge-point-select"
                            />

                            {selectedCharger &&
                            (isDawn(selectedCharger) || isLuna(selectedCharger)) &&
                            !getNfcLocked(selectedCharger, selectedCharger.connectors?.at(0)) ? (
                                <small className="p-text-secondary block mt-2">{text('msgTurnOnNfcForReader')}</small>
                            ) : null}
                        </div>

                        {isPartnerDomain ? (
                            <div className="field col-12">
                                <Dropdown
                                    label={text('lblOrganisation')}
                                    value={filters.companyId}
                                    options={organisations?.map(({ id, name }) => ({ value: id, label: name }))}
                                    onChange={(e) => applyFilter({ companyId: e.value })}
                                    checkmark={true}
                                    highlightOnSelect={false}
                                    filter
                                    id="organisation-select"
                                    className="w-full"
                                    showClear
                                    loading={isLoadingOrganisations}
                                    data-testid="organisation-select"
                                />
                            </div>
                        ) : null}

                        <div className="field col-12">
                            <label className="p-label" htmlFor="charge-point-select">
                                {text('lblDateFrom')}
                            </label>

                            <Calendar
                                className="w-full"
                                id="from"
                                value={filters.from ? new Date(filters.from) : undefined}
                                onChange={(e) =>
                                    applyFilter({
                                        from: e.value ? startOfDay(e.value).toISOString() : undefined,
                                    })
                                }
                                minDate={subYears(new Date(), 1)}
                                maxDate={filters.to ? new Date(filters.to) : new Date()}
                                showButtonBar
                                data-testid="date-from-input"
                            />
                        </div>

                        <div className="field col-12">
                            <label className="p-label" htmlFor="charge-point-select">
                                {text('lblDateTo')}
                            </label>

                            <Calendar
                                className="w-full"
                                id="to"
                                value={filters.to ? new Date(filters.to) : undefined}
                                onChange={(e) =>
                                    applyFilter({ to: e.value ? endOfDay(e.value).toISOString() : undefined })
                                }
                                minDate={filters.from ? new Date(filters.from) : subYears(new Date(), 1)}
                                maxDate={new Date()}
                                showButtonBar
                                data-testid="date-to-input"
                            />
                        </div>
                    </OverlayPanel>
                </div>
            </div>

            <Table
                value={filteredData}
                dataKey="rowKey"
                removableSort
                loading={rejectedTagsAreLoading}
                lazy
                paginator
                rows={rows}
                emptyMessage={text('msgNoRfidTagsFound')}
                selectionMode="checkbox"
                selection={selectedTags}
                onSelectionChange={(e) => setSelectedTags(e.value as RejectedAccessRow[])}
                first={page * rows}
                totalRecords={rejectedTags?.totalCount ?? 0}
                data-testid="rejected-rfid-tags"
                onPage={(e) => {
                    setFilters((prev) => ({ ...prev, page: e.page }))
                }}
            >
                <Column selectionMode="multiple" headerClassName="w-3rem" />
                <Column field="nfcTagId" header={text('lblId')} />
                <Column field="chargePointId" header={text('lblChargePointId')} />
                <Column
                    field="timeStamp"
                    header={text('lblRejectedAt')}
                    body={(tagData: RejectedAccess) => dateLocalize(tagData.timeStamp)}
                />
                <Column
                    body={(rowData: RejectedAccess) => {
                        return (
                            <Button
                                size="small"
                                icon="pi pi-trash"
                                rounded
                                outlined
                                severity="danger"
                                onClick={() => handleDeleteRejectedRFIDTag(rowData)}
                            />
                        )
                    }}
                ></Column>
            </Table>

            {partnerId ? (
                <AddRFIDDialog
                    visible={addRfidTagIsOpened}
                    partnerId={partnerId}
                    onSubmit={handleAddSelected}
                    onHide={() => setAddRfidTagIsOpened(false)}
                />
            ) : null}
        </>
    )
}
